import { CheckoutButton } from "@clerk/react/experimental";
import { ArrowRight, Check, Clock } from "lucide-react";
import { useState } from "react";

import {
  type BillingPeriod,
  FREE_PLAN_FEATURES,
  getPlusPlanId,
  isPlusPurchaseEnabled,
  PLUS_PLAN_FEATURES,
  PLUS_PRICE,
  PURCHASES_PAUSED_COPY,
  plusAnnualSavingsPercent,
} from "@sill/shared/components/billing/pricing-copy";

import { cn } from "../../lib/utils";

export interface PricingPanelProps {
  // When true, the Plus card swaps its CTA for a "You're on Plus" pill and
  // the Free card disables its CTA. Caller derives this from useEntitlements
  // — keeps the panel a pure render of the state it's handed.
  isPlusActive: boolean;
  // Free-plan CTA: in the modal surface this is "Continue free" → closes
  // the dialog. On marketing it's "Sign up free" → routes into the app.
  onFreeCta?: () => void;
  freeCtaLabel?: string;
  // Where to send the user after a successful Clerk checkout. The modal
  // typically wants the current pathname (deep-link continuity); marketing
  // wants the consumer app's dashboard.
  newSubscriptionRedirectUrl: string;
  // Called by the child CTA before Clerk opens its own checkout portal.
  onCheckoutStart?: (period: BillingPeriod) => void;
  initialBillingPeriod?: BillingPeriod;
  // Cosmetic: tighten the surface inside a modal where the dialog already
  // owns its own top padding. Marketing surface uses the default.
  density?: "default" | "compact";
}

// PricingPanel: the two-card pricing comparison surface used by the in-app
// UpgradeSheet (full-page modal) and the marketing /pricing page. Replaces
// Clerk's hosted <PricingTable> with a fully-owned visual matching the
// reference at docs/superpowers/specs/2026-05-12-sill-plus-design.md and the
// design comp at .design/pricing.html.
//
// CTA wiring uses Clerk Billing's <CheckoutButton> — we own the button look
// and click target; Clerk owns the checkout drawer that opens on click. The
// planId comes from VITE_CLERK_PLUS_PLAN_ID; missing-env falls back to a
// disabled CTA so misconfig fails closed instead of POSTing an empty planId.
export function PricingPanel({
  isPlusActive,
  onFreeCta,
  freeCtaLabel = "Continue free",
  newSubscriptionRedirectUrl,
  onCheckoutStart,
  initialBillingPeriod = "annual",
  density = "default",
}: PricingPanelProps) {
  // Default to annual on the in-app pricing surface, matching marketing
  // /pricing — annual leads with its effective monthly cost ($4.99/month)
  // and the toggle's "Save N%" pill makes the discount vs the $9.99/mo
  // rack rate explicit. A URL upgrade-intent still overrides this default.
  const [period, setPeriod] = useState<BillingPeriod>(initialBillingPeriod);
  const purchaseEnabled = isPlusPurchaseEnabled();
  const planId = getPlusPlanId();
  const priceData = period === "annual" ? PLUS_PRICE.annual : PLUS_PRICE.monthly;
  const headlinePrice = period === "annual" ? "$5" : "$10";

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-5xl",
        density === "compact" ? "pb-4" : "pb-12",
      )}
    >
      <header className="mx-auto max-w-2xl text-center">
        <h2 className="text-balance text-4xl font-[650] leading-[1.05] tracking-[-0.04em] sm:text-5xl">
          {headlinePrice} a month to{" "}
          <em className="not-italic text-primary">
            answer any question you need.
          </em>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-[1.0625rem] leading-snug text-muted-foreground">
          Free gets you the calculators, your verified bank connection, and
          the math on what you actually have. Plus removes the limits —
          unlimited questions and rate alerts on your terms.
        </p>
        {/* Trial line sits where the old "savings vary" disclaimer did.
            Dropping the outcome-flavored disclaimer along with the
            outcome-flavored claim keeps the surface RESPA-clean. */}
        <p className="mx-auto mt-2 max-w-lg text-[11px] leading-snug text-muted-foreground/80">
          14-day free trial. No card required. Cancel any time.
        </p>

        <BillingToggle
          period={period}
          onChange={setPeriod}
          disabled={!purchaseEnabled}
        />
      </header>

      {!purchaseEnabled ? (
        <div
          data-testid="purchases-paused-notice"
          className="mx-auto mt-10 max-w-xl rounded-2xl bg-muted/40 p-6 text-center"
        >
          <p className="font-medium text-foreground">
            {PURCHASES_PAUSED_COPY.title}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            {PURCHASES_PAUSED_COPY.subtitle}
          </p>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <FreePlanCard
            isPlusActive={isPlusActive}
            onFreeCta={onFreeCta}
            freeCtaLabel={freeCtaLabel}
          />
          <PlusPlanCard
            period={period}
            isPlusActive={isPlusActive}
            planId={planId}
            redirectUrl={newSubscriptionRedirectUrl}
            onCheckoutStart={onCheckoutStart}
            priceAmount={priceData.amount}
            priceSuffix={priceData.periodLabel}
            metaPrimary={priceData.metaPrimary}
            metaSecondary={
              "metaSecondary" in priceData ? priceData.metaSecondary : undefined
            }
            ctaLabel={priceData.ctaLabel}
          />
        </div>
      )}
    </div>
  );
}

function BillingToggle({
  period,
  onChange,
  disabled,
}: {
  period: BillingPeriod;
  onChange: (next: BillingPeriod) => void;
  disabled?: boolean;
}) {
  // Per-button backgrounds rather than a sliding thumb so the toggle sizes
  // correctly without measurement.
  const savingsPct = plusAnnualSavingsPercent();
  return (
    <div className="mt-5 flex justify-center">
      <div
        role="tablist"
        aria-label="Billing period"
        className={cn(
          "inline-flex rounded-full border border-border bg-card p-1 shadow-sm",
          disabled && "opacity-50",
        )}
      >
        {(["monthly", "annual"] as const).map((opt) => {
          const selected = period === opt;
          return (
            <button
              key={opt}
              type="button"
              role="tab"
              aria-selected={selected}
              data-testid={`billing-toggle-${opt}`}
              disabled={disabled}
              onClick={() => onChange(opt)}
              className={cn(
                "inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-2 text-[13px] font-semibold transition-colors duration-150",
                selected
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {opt === "monthly" ? "Monthly" : "Annual"}
              {opt === "annual" && savingsPct > 0 && (
                <span
                  data-testid="annual-savings-badge"
                  className={cn(
                    "rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none tabular-nums",
                    selected
                      ? "bg-background/20 text-background"
                      : "bg-primary/15 text-primary",
                  )}
                >
                  Save {savingsPct}%
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FreePlanCard({
  isPlusActive,
  onFreeCta,
  freeCtaLabel,
}: {
  isPlusActive: boolean;
  onFreeCta?: () => void;
  freeCtaLabel: string;
}) {
  return (
    <article
      data-testid="plan-card-free"
      className="relative flex flex-col rounded-[28px] bg-card p-8"
    >
      <div className="mb-2 text-base font-bold tracking-[-0.01em]">Free</div>
      <p className="mb-7 max-w-[32ch] text-[15px] leading-snug text-muted-foreground">
        Everything you need to start house-shopping with real numbers behind
        every decision.
      </p>

      <div className="mb-1 flex min-h-[64px] items-baseline gap-1.5">
        <span className="mt-2 self-start text-2xl font-semibold tracking-[-0.02em] text-foreground/60">
          $
        </span>
        <span className="text-[3.5rem] font-bold leading-none tracking-[-0.045em] tabular-nums">
          0
        </span>
        <span className="text-[15px] font-medium text-muted-foreground">
          forever
        </span>
      </div>
      <div className="mb-7 min-h-[20px] text-[13px] text-muted-foreground">
        No card. No trial clock.
      </div>

      <button
        type="button"
        onClick={onFreeCta}
        disabled={isPlusActive}
        className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl border border-border bg-muted text-[15px] font-semibold text-foreground transition-colors hover:border-foreground/40 hover:bg-card disabled:cursor-default disabled:opacity-50"
      >
        {freeCtaLabel}
      </button>
      <div className="mt-3 min-h-[18px] text-center text-xs text-muted-foreground">
        {isPlusActive
          ? "You're on Plus — Free is included."
          : "You're already on Free."}
      </div>

      <FeatureList
        heading="What you get"
        features={FREE_PLAN_FEATURES}
        variant="light"
      />
    </article>
  );
}

function PlusPlanCard({
  period,
  isPlusActive,
  planId,
  redirectUrl,
  onCheckoutStart,
  priceAmount,
  priceSuffix,
  metaPrimary,
  metaSecondary,
  ctaLabel,
}: {
  period: BillingPeriod;
  isPlusActive: boolean;
  planId: string | null;
  redirectUrl: string;
  onCheckoutStart?: (period: BillingPeriod) => void;
  priceAmount: string;
  priceSuffix: string;
  metaPrimary: string;
  metaSecondary?: string;
  ctaLabel: string;
}) {
  // Color tokens for the dark Plus card are inlined as arbitrary classes so
  // we don't pollute the global token surface with a one-off "deep teal"
  // palette. If we end up using these elsewhere, promote them to tokens.
  return (
    <article
      data-testid="plan-card-plus"
      className="relative flex flex-col overflow-hidden rounded-[28px] p-8 text-[#f4f7f3] shadow-md"
      style={{
        background: "linear-gradient(180deg, #133127 0%, #0d1f1a 100%)",
      }}
    >
      <div className="mb-2 flex items-center gap-3">
        <div className="text-base font-bold tracking-[-0.01em] text-white">
          Plus
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(142,220,196,0.14)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#8edcc4]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#8edcc4]" />
          Recommended
        </span>
      </div>
      <p className="mb-7 max-w-[32ch] text-[15px] leading-snug text-[rgba(244,247,243,0.62)]">
        For when you want the limits off — ask as much as you want and watch
        the market on your own thresholds.
      </p>

      <div className="mb-1 flex min-h-[64px] items-baseline gap-1.5">
        <span className="mt-2 self-start text-2xl font-semibold tracking-[-0.02em] text-[rgba(244,247,243,0.7)]">
          $
        </span>
        <span className="text-[3.5rem] font-bold leading-none tracking-[-0.045em] tabular-nums text-white">
          {priceAmount}
        </span>
        <span className="text-[15px] font-medium text-[rgba(244,247,243,0.55)]">
          {priceSuffix}
        </span>
      </div>
      <div className="mb-7 min-h-[20px] space-y-1 text-[13px] text-[rgba(244,247,243,0.62)]">
        <p>{metaPrimary}</p>
        {metaSecondary && <p>{metaSecondary}</p>}
      </div>

      {isPlusActive ? (
        <div
          data-testid="plus-active-pill"
          className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl border border-[rgba(142,220,196,0.4)] bg-[rgba(142,220,196,0.10)] text-[15px] font-semibold text-[#8edcc4]"
        >
          <Check className="mr-2 h-4 w-4" />
          You&rsquo;re on Plus
        </div>
      ) : planId ? (
        <CheckoutButton
          planId={planId}
          planPeriod={period === "annual" ? "annual" : "month"}
          newSubscriptionRedirectUrl={redirectUrl}
        >
          <button
            type="button"
            data-testid="plus-cta"
            onClick={() => onCheckoutStart?.(period)}
            className="group inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 text-[15px] font-semibold text-primary-foreground transition-colors hover:bg-[oklch(0.55_0.13_165)] active:bg-primary-dark"
          >
            {ctaLabel}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </CheckoutButton>
      ) : (
        <button
          type="button"
          disabled
          data-testid="plus-cta-disabled"
          className="inline-flex h-[52px] w-full items-center justify-center rounded-2xl bg-primary/60 text-[15px] font-semibold text-primary-foreground opacity-60"
        >
          Checkout unavailable
        </button>
      )}

      <FeatureList
        heading="Everything in Free, plus"
        features={PLUS_PLAN_FEATURES}
        variant="dark"
      />
    </article>
  );
}

function FeatureList({
  heading,
  features,
  variant,
}: {
  heading: string;
  features: typeof FREE_PLAN_FEATURES;
  variant: "light" | "dark";
}) {
  const dark = variant === "dark";
  return (
    <>
      <div
        className={cn(
          "mt-8 border-t pt-6 text-[12px] font-semibold uppercase tracking-[0.18em]",
          dark
            ? "border-white/[0.08] text-[rgba(244,247,243,0.5)]"
            : "border-border/60 text-muted-foreground",
        )}
      >
        {heading}
      </div>
      <ul className="mt-4 flex flex-col gap-4">
        {features.map((feature) => {
          const isSoon = feature.kind === "soon";
          return (
            <li
              key={feature.title}
              className="grid grid-cols-[22px_1fr] items-start gap-3 leading-snug"
            >
              <span
                className={cn(
                  "mt-0.5 inline-flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full",
                  dark
                    ? isSoon
                      ? "bg-white/[0.06] text-[rgba(244,247,243,0.4)]"
                      : "bg-[rgba(142,220,196,0.16)] text-[#8edcc4]"
                    : "bg-primary-soft text-primary",
                )}
              >
                {isSoon ? (
                  <Clock className="h-3 w-3" />
                ) : (
                  <Check className="h-3 w-3" strokeWidth={3} />
                )}
              </span>
              <span
                className={cn(
                  "text-[14.5px]",
                  dark ? "text-[#e8ede7]" : "text-foreground",
                )}
              >
                <strong
                  className={cn(
                    "font-[650]",
                    dark
                      ? isSoon
                        ? "text-[rgba(244,247,243,0.42)]"
                        : "text-white"
                      : "text-foreground",
                  )}
                >
                  {feature.title}
                  {isSoon && (
                    <span className="ml-2 inline-block translate-y-[-2px] rounded-full border border-white/10 bg-white/[0.06] px-2 py-[2px] align-middle text-[9.5px] font-bold uppercase tracking-[0.16em] text-[rgba(244,247,243,0.6)]">
                      Coming soon
                    </span>
                  )}
                </strong>
                <span
                  className={cn(
                    "mt-1 block text-[13px]",
                    dark
                      ? isSoon
                        ? "text-[rgba(244,247,243,0.42)]"
                        : "text-[rgba(244,247,243,0.55)]"
                      : "text-muted-foreground",
                  )}
                >
                  {feature.detail}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}
